import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import ImagePickerModal from "@/components/ImagePickerModal";
import {
  Bold, Italic, Strikethrough, List, ListOrdered, Quote,
  Heading2, Heading3, Undo, Redo, Link as LinkIcon, Minus, BookOpen, Image as ImageIcon,
} from "lucide-react";

const btn = "p-1.5 rounded hover:bg-slate-100 text-slate-600 disabled:opacity-40";
const active = "bg-sky-50 text-sky-800";

const ToolbarButton = ({ onClick, isActive, disabled, title, children }) => (
  <button type="button" onClick={onClick} disabled={disabled} title={title}
    className={`${btn} ${isActive ? active : ""}`}>{children}</button>
);

export default function RichTextEditor({ value, onChange, placeholder }) {
  const [glossaryOpen, setGlossaryOpen] = useState(false);
  const [imagePickerOpen, setImagePickerOpen] = useState(false);
  const [glossary, setGlossary] = useState([]);
  const [glossQuery, setGlossQuery] = useState("");
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3] } }),
      Link.configure({ openOnClick: false, HTMLAttributes: { class: "text-sky-700 underline" } }),
      // Preserve <img> tags from .docx uploads (and allow base64 data URIs as a fallback
      // when object storage upload fails). Without this, TipTap silently strips images
      // when admins edit a blog and hit save.
      Image.configure({ inline: false, allowBase64: true, HTMLAttributes: { class: "blog-image" } }),
    ],
    content: value || "<p></p>",
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
    editorProps: {
      attributes: {
        class: "prose prose-slate max-w-none min-h-[160px] p-3 focus:outline-none",
      },
    },
  });

  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if (value !== current && value != null) {
      editor.commands.setContent(value || "<p></p>", false);
    }
  }, [value, editor]);

  useEffect(() => {
    if (glossaryOpen && glossary.length === 0) {
      api.get("/glossary").then(r => setGlossary(r.data));
    }
  }, [glossaryOpen, glossary.length]);

  if (!editor) return null;

  const setLink = () => {
    const url = window.prompt("Link URL (leave empty to remove)");
    if (url === null) return;
    if (url === "") { editor.chain().focus().extendMarkRange("link").unsetLink().run(); return; }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  const insertImage = () => {
    setImagePickerOpen(true);
  };

  const handleImagePicked = (url) => {
    if (!url) return;
    editor.chain().focus().setImage({ src: url }).run();
  };

  const B = ToolbarButton;

  return (
    <div className="border border-slate-300 rounded-md bg-white">
      <div className="flex flex-wrap items-center gap-0.5 border-b border-slate-200 px-2 py-1">
        <B onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} isActive={editor.isActive("heading", { level: 2 })} title="Heading 2"><Heading2 className="w-4 h-4"/></B>
        <B onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} isActive={editor.isActive("heading", { level: 3 })} title="Heading 3"><Heading3 className="w-4 h-4"/></B>
        <span className="w-px h-5 bg-slate-200 mx-1"/>
        <B onClick={() => editor.chain().focus().toggleBold().run()} isActive={editor.isActive("bold")} title="Bold"><Bold className="w-4 h-4"/></B>
        <B onClick={() => editor.chain().focus().toggleItalic().run()} isActive={editor.isActive("italic")} title="Italic"><Italic className="w-4 h-4"/></B>
        <B onClick={() => editor.chain().focus().toggleStrike().run()} isActive={editor.isActive("strike")} title="Strikethrough"><Strikethrough className="w-4 h-4"/></B>
        <B onClick={setLink} isActive={editor.isActive("link")} title="Link"><LinkIcon className="w-4 h-4"/></B>
        <span className="w-px h-5 bg-slate-200 mx-1"/>
        <B onClick={() => editor.chain().focus().toggleBulletList().run()} isActive={editor.isActive("bulletList")} title="Bulleted list"><List className="w-4 h-4"/></B>
        <B onClick={() => editor.chain().focus().toggleOrderedList().run()} isActive={editor.isActive("orderedList")} title="Numbered list"><ListOrdered className="w-4 h-4"/></B>
        <B onClick={() => editor.chain().focus().toggleBlockquote().run()} isActive={editor.isActive("blockquote")} title="Blockquote"><Quote className="w-4 h-4"/></B>
        <B onClick={() => editor.chain().focus().setHorizontalRule().run()} title="Divider"><Minus className="w-4 h-4"/></B>
        <B onClick={insertImage} title="Insert image"><ImageIcon className="w-4 h-4"/></B>
        <B onClick={() => setGlossaryOpen(true)} title="Insert glossary link"><BookOpen className="w-4 h-4"/></B>
        <span className="flex-1"/>
        <B onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} title="Undo"><Undo className="w-4 h-4"/></B>
        <B onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} title="Redo"><Redo className="w-4 h-4"/></B>
      </div>
      <EditorContent editor={editor} placeholder={placeholder}/>

      <ImagePickerModal open={imagePickerOpen}
                        onClose={() => setImagePickerOpen(false)}
                        onPick={handleImagePicked}/>

      {glossaryOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={() => setGlossaryOpen(false)}>
          <div onClick={e => e.stopPropagation()} className="bg-white border border-slate-200 rounded-lg w-full max-w-md p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="font-heading font-semibold text-slate-900">Insert glossary link</div>
              <button onClick={() => setGlossaryOpen(false)} className="text-slate-500 hover:text-slate-900">×</button>
            </div>
            <input value={glossQuery} onChange={e => setGlossQuery(e.target.value)} placeholder="Search terms…" className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm mb-2"/>
            <div className="max-h-64 overflow-auto divide-y divide-slate-100">
              {glossary.filter(g => g.term.toLowerCase().includes(glossQuery.toLowerCase())).map(g => (
                <button key={g.term_id} onClick={() => {
                  const { from, to, empty } = editor.state.selection;
                  const text = empty ? g.term : editor.state.doc.textBetween(from, to);
                  const html = `<span class="gloss-term" data-term-id="${g.term_id}" data-slug="${g.slug}">${text}</span>&nbsp;`;
                  editor.chain().focus().insertContent(html).run();
                  setGlossaryOpen(false);
                }} className="block w-full text-left px-2 py-2 hover:bg-slate-50 text-sm">
                  <div className="font-medium text-slate-900">{g.term}</div>
                  <div className="text-xs text-slate-500 line-clamp-1">{g.definition}</div>
                </button>
              ))}
              {glossary.length === 0 && <div className="text-xs text-slate-400 p-2">No glossary terms yet. Add some in Admin → Glossary.</div>}
            </div>
            <div className="text-xs text-slate-400 mt-2">Select text first to link it, or place cursor to insert the term name.</div>
          </div>
        </div>
      )}
    </div>
  );
}
